import { LineItem } from "./order";
import { ProductType } from "../products/product";
import { CustomUmbrellaOrder } from "../configurator/custom_umbrella";
import { FabricSwatch } from "../products/fabric";
import * as option from "../configurator/configurator_options";
import {
  getFinishName,
  getFinialFinishDisplayName
} from "@/utility/helpers";
import {
  hasStructuredCanopy,
  hasEqualMainTopFabrics,
  hasEqualMainMiddleFabrics,
  hasEqualTopMiddleFabrics,
  hasDesignerLayout,
  getDesignerLayoutName
} from "../configurator/canopy_display";

/**
 * FUPOF-191 — plain-text spell-out of a custom umbrella's configuration, mirroring
 * the Cart "Details" tooltip (Cart.vue). It is prepended to a line's NetSuite
 * Production Notes (see composeLineNotes) so reps and production can read what the
 * TEMP-CUST-ITEM placeholder line actually is — canopy fabrics with mfr codes,
 * vents, trim, valance, binding, fringe, and frame options.
 *
 * Returns "" for anything without a renderable configuration (non-umbrella,
 * ns_locked, or NS-parsed lines whose fabrics are bare code strings), matching
 * exactly when the tooltip is shown.
 *
 * Format constraint: no emitted line may begin with "Item Notes: " or any
 * "<FIELD> Notes: " heading (the NoteFields enum uppercased), because
 * decomposeLineNotes keys off those. Every line here uses "Label: value" phrasing
 * or is indented, so none collide.
 */

const INDENT = "  ";

/** "Sunbrella Jockey Red (5403 / R-020)" — display name with mfr code(s). */
function formatFabric(fabric?: FabricSwatch | null): string {
  if (!fabric) return "";
  const display = fabric.name?.display ?? "";
  const codes = fabric.alt_mfr_code
    ? `${fabric.mfr_code} / ${fabric.alt_mfr_code}`
    : fabric.mfr_code;
  return `${display} (${codes})`.trim();
}

function formatFabricList(fabrics?: FabricSwatch[] | null): string {
  if (!fabrics || fabrics.length === 0) return "";
  return fabrics.map(formatFabric).join(", ");
}

export function buildLineDescription(item: LineItem): string {
  // Match the Cart tooltip's visibility exactly: umbrellas only, never a locked
  // line, and only when the canopy carries real swatch objects.
  if (item.product_type !== ProductType.Umbrella) return "";
  if (item.ns_locked) return "";
  if (!hasStructuredCanopy(item)) return "";

  const details = item.item_details as CustomUmbrellaOrder;
  const canopy = details?.canopy as any;
  const frame = details?.frame as any;
  const layout = details?.layout as any;
  if (!canopy) return "";

  const lines: string[] = [];

  // --- Canopy ---
  lines.push("Canopy");

  // Main fabric label collapses in matching vent fabrics, mirroring the tooltip.
  const equalMainTop = hasEqualMainTopFabrics(canopy);
  const equalMainMiddle = hasEqualMainMiddleFabrics(canopy);
  const equalTopMiddle = hasEqualTopMiddleFabrics(canopy);

  let mainLabel = "Main";
  if (equalMainTop && !equalMainMiddle) {
    mainLabel += " & Top Vent";
  }
  if (equalMainMiddle && !equalMainTop) {
    mainLabel += " & Middle Vent";
  }
  if (equalTopMiddle && equalMainTop) {
    mainLabel += " & Double Vent";
  }
  const mainPlural =
    canopy.mainFabric.length > 1 || (equalTopMiddle && equalMainTop);
  mainLabel += ` Fabric${mainPlural ? "s" : ""}`;
  lines.push(`${INDENT}${mainLabel}: ${formatFabricList(canopy.mainFabric)}`);

  // Designer trim layout.
  const preset = layout?.preset ?? null;
  if (hasDesignerLayout(preset)) {
    const layoutName = getDesignerLayoutName(preset);
    if (layoutName === "Single Trim") {
      lines.push(
        `${INDENT}${layoutName} Designer Layout: ${formatFabric(
          canopy.mainTrimFabric
        )}`
      );
    } else {
      lines.push(`${INDENT}${layoutName} Designer Layout:`);
      lines.push(
        `${INDENT}Inner Trim: ${formatFabric(canopy.mainTrimFabricInner)}`
      );
      lines.push(
        `${INDENT}Outer Trim: ${formatFabric(canopy.mainTrimFabric)}`
      );
    }
  }

  // Top (& Middle) vent fabric, when not already folded into Main.
  if (!equalMainTop && canopy.ventTopFabric && canopy.ventTopFabric.length > 0) {
    let ventLabel = "Top";
    if (equalTopMiddle && !equalMainTop) {
      ventLabel += " & Middle";
    }
    lines.push(
      `${INDENT}${ventLabel} Vent Fabrics: ${formatFabric(
        canopy.ventTopFabric[0]
      )}`
    );
  }

  // Distinct double (middle) vent fabric.
  if (
    canopy.ventMiddleFabric &&
    canopy.ventMiddleFabric.length > 0 &&
    !equalTopMiddle &&
    !equalMainMiddle
  ) {
    const label = `Double Vent Fabric${
      canopy.ventMiddleFabric.length > 1 ? "s" : ""
    }`;
    lines.push(`${INDENT}${label}: ${formatFabricList(canopy.ventMiddleFabric)}`);
  }

  if (canopy.undersideFabric) {
    lines.push(
      `${INDENT}Underside Fabric: ${formatFabric(canopy.undersideFabric)}`
    );
  }

  if (canopy.valance && canopy.valance !== "none") {
    const valanceName = option.getValanceDisplayName(canopy.valance);
    const label = `${valanceName} Valance Fabric${
      canopy.valanceFabric && canopy.valanceFabric.length > 1 ? "s" : ""
    }`;
    lines.push(`${INDENT}${label}: ${formatFabricList(canopy.valanceFabric)}`);
    lines.push(
      `${INDENT}Binding Fabric: ${formatFabric(canopy.bindingFabric)}`
    );
  }

  if (canopy.fringe && canopy.fringe !== "none") {
    const fringeName = option.getFringeDisplayName(canopy.fringe);
    const label = `${fringeName} Fringe Fabric${
      canopy.fringeFabric && canopy.fringeFabric.length > 1 ? "s" : ""
    }`;
    lines.push(`${INDENT}${label}: ${formatFabricList(canopy.fringeFabric)}`);
  }

  if (canopy.rib === "grommet") {
    lines.push(`${INDENT}Rib: grommets`);
  }

  // --- Frame ---
  if (frame) {
    lines.push("Frame");

    if (frame.finish) {
      lines.push(
        `${INDENT}Finish: ${getFinishName(
          frame.finish
        )} (${String(frame.finish).toUpperCase()})`
      );
    }

    if (frame.bottomPole === "bar") {
      lines.push(`${INDENT}Bottom Pole: bar height`);
    }

    if (frame.finialFinish || frame.finial === "Vertex") {
      const finishName = frame.finialFinish
        ? `${getFinialFinishDisplayName(frame.finialFinish)} `
        : "";
      lines.push(`${INDENT}Finial: ${finishName}${frame.finial ?? ""}`.trimEnd());
    }

    if (frame.spigot) {
      lines.push(
        `${INDENT}Spigot: ${option.getSpigotDisplayName(frame.spigot)}`
      );
    }
  }

  return lines.join("\n");
}
